import { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Calendar } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { CALENDLY_EMBED_URL, CALENDLY_PROFILE_URL } from '@/config/calendly';

export function CalendlyController() {
  const [isOpen, setIsOpen] = useState(false);
  const [isCalendlyReady, setIsCalendlyReady] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Attendre que Calendly soit prêt quand le modal s'ouvre
  useEffect(() => {
    if (!isOpen) return;

    let attempts = 0;
    const maxAttempts = 20; // 2 secondes max

    const checkCalendly = () => {
      if (window.Calendly?.initInlineWidget) {
        setIsCalendlyReady(true);
        return;
      }
      attempts++;
      if (attempts < maxAttempts) {
        setTimeout(checkCalendly, 100);
      } else {
        // Fallback: ouvrir dans un nouvel onglet
        setIsOpen(false);
        window.open(CALENDLY_PROFILE_URL, '_blank', 'noopener,noreferrer');
      }
    };

    checkCalendly();
  }, [isOpen]);

  // Initialiser le widget inline quand Calendly est prêt
  useEffect(() => {
    if (!isOpen || !isCalendlyReady || !containerRef.current) return;

    // Vider le conteneur avant d'initialiser
    containerRef.current.innerHTML = '';

    if (window.Calendly?.initInlineWidget) {
      window.Calendly.initInlineWidget({
        url: CALENDLY_EMBED_URL,
        parentElement: containerRef.current,
      });
    }
  }, [isOpen, isCalendlyReady]);

  // Reset l'état quand le modal se ferme
  useEffect(() => {
    if (!isOpen) {
      setIsCalendlyReady(false);
    }
  }, [isOpen]);

  return (
    <>
      <div className="fixed bottom-4 right-4 z-40">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                onClick={() => setIsOpen(true)}
                className="h-14 w-14 rounded-full shadow-xl bg-gradient-to-br from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 transition-all duration-300 border-2 border-white/20"
                title="Prendre rendez-vous"
              >
                <Calendar className="h-6 w-6 text-white" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="left" className="bg-white border shadow-lg">
              <p className="font-medium">Prendre rendez-vous</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-3xl w-[95vw] h-[85vh] p-0 overflow-hidden">
          <div 
            ref={containerRef}
            className="w-full h-full min-h-[500px]"
          />
          {!isCalendlyReady && (
            <div className="absolute inset-0 flex items-center justify-center bg-background">
              <div className="text-center space-y-4">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
                <p className="text-muted-foreground">Chargement du calendrier...</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
